import React, { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { ZoomIn, ZoomOut, Maximize } from 'lucide-react';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configuración del Worker desde CDN para evitar problemas de compilación
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface ContratoPasoProps {
  pdfUrl: string;
  aceptoTerminos: boolean;
  onAceptoTerminosChange: (value: boolean) => void;
}

const ContratoPaso: React.FC<ContratoPasoProps> = ({ pdfUrl, aceptoTerminos, onAceptoTerminosChange }) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);
  const [scale, setScale] = useState<number>(1.0);

  // Efecto para calcular el ancho del contenedor y que el PDF sea responsive
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        // En móviles quitamos un poco más de margen para aprovechar la pantalla
        const padding = window.innerWidth < 640 ? 20 : 40;
        setContainerWidth(containerRef.current.offsetWidth - padding);
      }
    };

    updateWidth();
    
    // Observer para detectar cambios de tamaño (rotación de móvil, etc)
    const resizeObserver = new ResizeObserver(() => {
      updateWidth();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    window.addEventListener('resize', updateWidth);
    return () => {
      window.removeEventListener('resize', updateWidth);
      resizeObserver.disconnect();
    };
  }, []);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
  }

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.2, 2.5));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.5));
  const handleResetZoom = () => setScale(1.0);

  return (
    <Card className='pb-1'>
      <CardHeader className='pb-1'>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <CardTitle>Paso 4: Revisión del Contrato</CardTitle>
            <CardDescription>Por favor, lee y acepta los términos del servicio.</CardDescription>
          </div>
          
          {/* Controles de Zoom */}
          <div className="flex items-center bg-muted rounded-md p-1 self-end sm:self-auto">
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8" 
              onClick={handleZoomOut}
              title="Reducir"
            >
              <ZoomOut className="h-4 w-4" />
            </Button>
            <span className="text-xs font-medium w-12 text-center">
              {Math.round(scale * 100)}%
            </span>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8" 
              onClick={handleZoomIn}
              title="Ampliar"
            >
              <ZoomIn className="h-4 w-4" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 ml-1" 
              onClick={handleResetZoom}
              title="Ajustar"
            >
              <Maximize className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        <div ref={containerRef} className="border rounded-md bg-slate-100 overflow-hidden">
          <ScrollArea className="h-[450px] sm:h-[550px] w-full">
            <div className="flex justify-center p-2 sm:p-4">
              {pdfUrl ? (
                  <Document
                    file={pdfUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    loading={<div className="p-10 text-center text-sm">Cargando documento legal...</div>}
                    error={<div className="p-10 text-center text-red-500 text-sm">Error al cargar el PDF.</div>}
                  >
                    {Array.from(new Array(numPages), (_, index) => (
                      <Page
                        key={`page_${index + 1}`}
                        pageNumber={index + 1}
                        width={containerWidth > 0 ? containerWidth * scale : undefined}
                        className="mb-4 shadow-lg"
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                      />
                    ))}
                  </Document>
                ) : (
                  <div className="p-10 text-center">Preparando archivo...</div>
                )}
            </div>
          </ScrollArea>
        </div>
        
        <div className="flex items-start space-x-3 p-4 bg-muted/50 rounded-lg">
          <Checkbox 
            id="acepto-terminos" 
            checked={aceptoTerminos}
            onCheckedChange={(checked) => onAceptoTerminosChange(checked as boolean)}
          />
          <div className="space-y-1 leading-none">
            <Label 
              htmlFor="acepto-terminos" 
              className="text-sm font-medium cursor-pointer"
            >
              He leído y acepto los términos y condiciones del contrato de alquiler
            </Label>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ContratoPaso;
